import { Box, Tab, Tabs, Typography } from '@mui/material';
import React from 'react';
import { themeColors } from '../JuvareTheme';
import { LayoutRows2 } from '../LayoutRows2';
import { PageHeader } from '../PageHeader';
import { useCommonWebTranslation } from '../hooks';
/**
 * Renders a page layout with a scrollable tab bar and tab content.
 * Handles badge rendering, tab selection, and optional header and layout configuration.
 *
 * @param {TabsPageProps} props - The props for the TabsPage component.
 * @param {string | null} props.selectedTabId - ID of the selected tab, or `null` to use `isTabSelected`.
 * @param {(tab: TabType) => boolean} [props.isTabSelected] - Optional function to determine the selected tab.
 * @param {(tab: TabType) => void} [props.onSelectTab] - Callback invoked when a new tab is selected.
 * @param {TabType[]} props.tabs - Array of tab definitions, each including id, label, and optional badge and onClick handler.
 * @param {React.ReactNode} props.children - The content to render below the tab header.
 * @param {string} [props.title] - Optional title to display inside a default header.
 * @param {React.ReactNode} [props.header] - Optional custom header component. Overrides the default header.
 * @param {string} [props.gradient] - Optional gradient type for the default `PageHeader`.
 * @param {boolean} [props.fullHeightChildren] - Whether to render children in a full-height scrollable layout.
 * @param {SxProps<Theme>} [props.sx] - Optional styling applied to the tab container.
 *
 * @returns {JSX.Element} A page layout component with tabs and optional custom header or full-height layout.
 */
export const TabsPage = (props) => {
    var _a, _b, _c;
    const { t } = useCommonWebTranslation();
    let selectedTabId = props.selectedTabId;
    if (selectedTabId === null && props.isTabSelected) {
        for (const tab of props.tabs) {
            if (props.isTabSelected(tab)) {
                selectedTabId = tab.id;
                break;
            }
        }
    }
    const selectedTab = selectedTabId ? props.tabs.find((tab) => tab.id === selectedTabId) : undefined;
    const handleTabChange = (event, value) => {
        var _a, _b;
        const selectedTab = props.tabs.find((tab) => tab.id === value);
        if (selectedTab) {
            (_a = selectedTab.onClick) === null || _a === void 0 ? void 0 : _a.call(selectedTab);
            (_b = props.onSelectTab) === null || _b === void 0 ? void 0 : _b.call(props, selectedTab);
        }
    };
    const handleTabAriaLabel = (tab) => {
        if (tab.badge != null) {
            if (typeof tab.badge === 'number') {
                return t('common.badge', { value: tab.label, count: tab.badge });
            }
            return t('common.badgeForCustomCount', { value: tab.label, customCount: tab.badge });
        }
        return undefined;
    };
    const header = (React.createElement(React.Fragment, null, (_a = props.header) !== null && _a !== void 0 ? _a : (React.createElement(PageHeader, { gradient: (_b = props.gradient) !== null && _b !== void 0 ? _b : 'blue-horizontal-left' },
        React.createElement(Typography, { variant: 'h1', sx: { p: 4 } }, props.title))),
        React.createElement(Box, { sx: [
                {
                    pl: 4,
                    pr: 4,
                    bgcolor: 'background.paper',
                    boxShadow: (theme) => theme.shadows[1],
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ] },
            React.createElement(Tabs, { variant: 'scrollable', value: selectedTabId !== null && selectedTabId !== void 0 ? selectedTabId : false, onChange: handleTabChange }, props.tabs.map((tab) => (React.createElement(Tab, { key: tab.id, value: tab.id, label: tab.label, "data-test": tab['data-test'], iconPosition: tab.badge != null ? 'end' : undefined, "aria-label": typeof tab.label === 'object' ? undefined : handleTabAriaLabel(tab), icon: tab.badge != null ? (React.createElement(Typography, { variant: 'caption', sx: {
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        minWidth: '20px',
                        height: '20px',
                        marginLeft: '4px',
                        padding: '0px 6px',
                        borderRadius: '10px',
                        lineHeight: 1,
                        color: themeColors.text.inverse,
                        backgroundColor: themeColors.accent.red.bold,
                    }, "data-test": 'badge', "aria-hidden": true }, tab.badge)) : undefined })))))));
    if (props.fullHeightChildren) {
        return (React.createElement(LayoutRows2, { row1: header, row2: props.children, fullHeightRow: 'row2', row2Scrollable: (_c = selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.fullHeightScrollable) !== null && _c !== void 0 ? _c : false }));
    }
    else {
        return (React.createElement(React.Fragment, null,
            header,
            props.children));
    }
};
